/**
 * product-reviews-slider.js
 *
 * Lightweight, dependency-free slider for the Product Reviews Slider section.
 * Supports multiple independent instances on a single page.
 * Uses a Web Component (custom element) pattern for clean scoping.
 */

(function () {
  'use strict';

  if (customElements.get('prs-slider')) return; // already registered

  class PrsSlider extends HTMLElement {
    constructor() {
      super();
      this._index = 0;
      this._timer = null;
      this._dragging = false;
      this._dragStartX = 0;
      this._dragCurrentX = 0;
      this._dragThreshold = 50;
    }

    connectedCallback() {
      this._track = this.querySelector('.prs-track');
      this._cards = Array.from(this.querySelectorAll('.prs-card'));
      this._dotsContainer = this.querySelector('.prs-dots');
      this._prevBtn = this.querySelector('.prs-arrow--prev');
      this._nextBtn = this.querySelector('.prs-arrow--next');

      if (!this._track || this._cards.length === 0) return;

      // Config from data attributes
      this._autoplay = this.dataset.autoplay === 'true';
      this._autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) || 4000;
      this._visibleDesktop = Math.max(1, parseInt(this.dataset.visibleDesktop, 10) || 3);
      this._visibleMobile = Math.max(1, parseInt(this.dataset.visibleMobile, 10) || 1);

      // Remove no-js class now that JS is available
      this.classList.remove('prs-no-js');

      this._buildDots();
      this._bindEvents();

      // iOS Safari: offsetWidth kan 0 zijn tijdens connectedCallback omdat
      // de browser de layout nog niet heeft afgerond. requestAnimationFrame
      // wacht tot na de eerste paint zodat de breedte correct is.
      requestAnimationFrame(() => {
        this._applyCardWidths();
        this._goTo(0);
        if (this._autoplay) this._startAutoplay();
      });

      // Handle resize
      this._resizeObserver = new ResizeObserver(() => {
        this._applyCardWidths();
        this._goTo(this._index);
      });
      this._resizeObserver.observe(this);
    }

    disconnectedCallback() {
      this._stopAutoplay();
      if (this._resizeObserver) this._resizeObserver.disconnect();
    }

    _getVisible() {
      return window.innerWidth < 768 ? this._visibleMobile : this._visibleDesktop;
    }

    _getGap() {
      // Read the computed gap from the track (fallback 20px)
      const trackStyle = window.getComputedStyle(this._track);
      return parseFloat(trackStyle.columnGap || trackStyle.gap || '20') || 20;
    }

    _applyCardWidths() {
      const visible = this._getVisible();
      const gap = this._getGap();
      const containerWidth = this.offsetWidth || 0;
      if (!containerWidth) return;

      const cardWidth = (containerWidth - gap * (visible - 1)) / visible;
      this._cards.forEach((card) => {
        card.style.width = cardWidth + 'px';
        card.style.minWidth = cardWidth + 'px';
      });
    }

    _maxIndex() {
      const visible = this._getVisible();
      return Math.max(0, this._cards.length - visible);
    }

    _getOffset(index) {
      if (this._cards.length === 0) return 0;
      const gap = this._getGap();
      const cardWidth = this._cards[0].offsetWidth;
      return index * (cardWidth + gap);
    }

    _goTo(index) {
      const max = this._maxIndex();
      this._index = Math.min(Math.max(0, index), max);
      const offset = this._getOffset(this._index);
      this._track.style.transform = `translateX(-${offset}px)`;
      this._updateArrows();
      this._updateDots();
    }

    _prev() {
      this._goTo(this._index - 1);
      if (this._autoplay) this._restartAutoplay();
    }

    _next() {
      const max = this._maxIndex();
      if (this._index >= max) {
        this._goTo(0); // loop back
      } else {
        this._goTo(this._index + 1);
      }
      if (this._autoplay) this._restartAutoplay();
    }

    _updateArrows() {
      if (!this._prevBtn || !this._nextBtn) return;
      const max = this._maxIndex();
      const isFirst = this._index === 0;
      const isLast = this._index >= max;

      this._prevBtn.disabled = isFirst && !this._autoplay;
      this._prevBtn.setAttribute('aria-disabled', (isFirst && !this._autoplay).toString());
      this._nextBtn.disabled = isLast && !this._autoplay;
      this._nextBtn.setAttribute('aria-disabled', (isLast && !this._autoplay).toString());
    }

    _buildDots() {
      if (!this._dotsContainer) return;
      this._dotsContainer.innerHTML = '';
      const max = this._maxIndex();
      const count = max + 1;

      for (let i = 0; i < count; i++) {
        const btn = document.createElement('button');
        btn.className = 'prs-dot';
        btn.setAttribute('aria-label', `Ga naar review ${i + 1}`);
        btn.addEventListener('click', () => {
          this._goTo(i);
          if (this._autoplay) this._restartAutoplay();
        });
        this._dotsContainer.appendChild(btn);
      }
    }

    _updateDots() {
      if (!this._dotsContainer) return;
      const dots = this._dotsContainer.querySelectorAll('.prs-dot');
      dots.forEach((dot, i) => {
        dot.classList.toggle('prs-dot--active', i === this._index);
        dot.setAttribute('aria-current', i === this._index ? 'true' : 'false');
      });
    }

    _startAutoplay() {
      this._stopAutoplay();
      this._timer = setInterval(() => this._next(), this._autoplaySpeed);
    }

    _stopAutoplay() {
      if (this._timer) {
        clearInterval(this._timer);
        this._timer = null;
      }
    }

    _restartAutoplay() {
      if (this._autoplay) {
        this._stopAutoplay();
        this._startAutoplay();
      }
    }

    _bindEvents() {
      if (this._prevBtn) {
        this._prevBtn.addEventListener('click', () => this._prev());
      }
      if (this._nextBtn) {
        this._nextBtn.addEventListener('click', () => this._next());
      }

      // Pause autoplay on hover / focus
      this.addEventListener('mouseenter', () => this._stopAutoplay());
      this.addEventListener('mouseleave', () => { if (this._autoplay) this._startAutoplay(); });
      this.addEventListener('focusin', () => this._stopAutoplay());
      this.addEventListener('focusout', () => { if (this._autoplay) this._startAutoplay(); });

      // Touch / mouse drag
      this._track.addEventListener('pointerdown', (e) => this._onDragStart(e));
      this._track.addEventListener('pointermove', (e) => this._onDragMove(e));
      this._track.addEventListener('pointerup', (e) => this._onDragEnd(e));
      this._track.addEventListener('pointercancel', () => this._cancelDrag());

      // Keyboard navigation
      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { this._prev(); e.preventDefault(); }
        if (e.key === 'ArrowRight') { this._next(); e.preventDefault(); }
      });
    }

    _onDragStart(e) {
      this._dragging = true;
      this._dragStartX = e.clientX;
      this._dragCurrentX = e.clientX;
      this._track.setPointerCapture(e.pointerId);
      this._stopAutoplay();
      this._track.style.transition = 'none';
    }

    _onDragMove(e) {
      if (!this._dragging) return;
      this._dragCurrentX = e.clientX;
      const delta = this._dragStartX - this._dragCurrentX;
      const baseOffset = this._getOffset(this._index);
      this._track.style.transform = `translateX(-${baseOffset + delta}px)`;
    }

    _onDragEnd(e) {
      if (!this._dragging) return;
      this._dragging = false;
      this._track.style.transition = '';
      const delta = this._dragStartX - e.clientX;
      if (Math.abs(delta) > this._dragThreshold) {
        if (delta > 0) this._next();
        else this._prev();
      } else {
        this._goTo(this._index);
      }
      if (this._autoplay) this._restartAutoplay();
    }

    _cancelDrag() {
      if (!this._dragging) return;
      this._dragging = false;
      this._track.style.transition = '';
      this._goTo(this._index);
    }
  }

  customElements.define('prs-slider', PrsSlider);
})();
